
import React from 'react'
import { mdx } from '@mdx-js/react'

/* @jsxRuntime classic */
/* @jsx mdx */

import DocsBeta from 'src/components/docs/DocsBeta/index.tsx';
import styles from '../../core-exchange.module.scss';
export const metadata = {
  toc: true,
  subnav: true,
  title: 'App Directory API',
  layout: 'guide',
  description: 'Programmatic access to connected application metadata',
  parentTocLevel: 2,
  childTocLevel: 3,
  secondLevelToc: true,
  alwaysExpand: true
};
const makeShortcode = name => function MDXDefaultShortcode(props) {
      console.warn("Component " + name + " was not imported, exported, or provided by MDXProvider as global scope")
      return <div {...props}/>
    };
const Header = makeShortcode("Header");
const Schema = makeShortcode("Schema");
const layoutProps = {
  metadata
};
const MDXLayout = "wrapper"
export default function MDXContent({
  components,
  ...props
}) {
  return <MDXLayout {...layoutProps} {...props} components={components} mdxType="MDXLayout">


    <div className={styles.page}>
      <Header title='App Directory API' subtitle={metadata.description} mdxType="Header" />
      <h2 {...{
        "id": "overview"
      }}>{`Overview`}</h2>
      <p>{`App Directory gives you visibility into which Plaid-powered applications your customers have connected to, without implementing `}<a parentName="p" {...{
          "href": "https://datatracker.ietf.org/doc/html/rfc7591"
        }}>{`Dynamic Client Registration`}</a>{`. Plaid manages application onboarding and risk review; you get read access to the resulting metadata for your own risk and compliance needs.`}</p>
      <p>{`Single-institution accounts can also get this data with no code, in the `}<a parentName="p" {...{
          "href": "/core-exchange/docs/permissions-manager#dashboard-access-single-institution-only"
        }}>{`Data Partner Dashboard`}</a>{`. This page covers the API, which is available to every integration model, including platforms.`}</p>
      <p>{`App Directory is available in Production only after you've completed your Core Exchange integration. To test before then, use `}<a parentName="p" {...{
          "href": "#testing"
        }}>{`Sandbox`}</a>{`.`}</p>
      <h2 {...{
        "id": "authentication"
      }}>{`Authentication`}</h2>
      <p>{`Get access to the Data Partner Dashboard and complete your Core Exchange integration, then find your `}<inlineCode parentName="p">{`client_id`}</inlineCode>{` and `}<inlineCode parentName="p">{`secret`}</inlineCode>{` on the `}<a parentName="p" {...{
          "href": "https://dashboard.plaid.com/developers/keys"
        }}>{`Developer > Keys`}</a>{` tab. Include both in the headers of every request as `}<inlineCode parentName="p">{`PLAID-CLIENT-ID`}</inlineCode>{` and `}<inlineCode parentName="p">{`PLAID-SECRET`}</inlineCode>{`.`}</p>
      <h2 {...{
        "id": "endpoints"
      }}>{`Endpoints`}</h2>
      <table>
        <thead parentName="table">
          <tr parentName="thead">
            <th parentName="tr" {...{
              "align": null
            }}>{`Endpoint`}</th>
            <th parentName="tr" {...{
              "align": null
            }}>{`Functionality`}</th>
          </tr>
        </thead>
        <tbody parentName="table">
          <tr parentName="tbody">
            <td parentName="tr" {...{
              "align": null
            }}><a parentName="td" {...{
                "href": "#get-a-list-of-applications-with-fdxrecipients"
              }}><inlineCode parentName="a">{`GET /fdx/recipients`}</inlineCode></a></td>
            <td parentName="tr" {...{
              "align": null
            }}>{`List all applications your customers have connected to`}</td>
          </tr>
          <tr parentName="tbody">
            <td parentName="tr" {...{
              "align": null
            }}><a parentName="td" {...{
                "href": "#get-a-single-application-with-fdxrecipientrecipientid"
              }}><inlineCode parentName="a">{`GET /fdx/recipient/{recipientId}`}</inlineCode></a></td>
            <td parentName="tr" {...{
              "align": null
            }}>{`Get details for a single application`}</td>
          </tr>
        </tbody>
      </table>
      <h3 {...{
        "id": "get-a-list-of-applications-with-fdxrecipients"
      }}>{`Get a list of applications with `}<inlineCode parentName="h3">{`/fdx/recipients`}</inlineCode></h3>
      <p>{`We recommend rerunning this report daily to keep your records current.`}</p>
      <Schema method='GET' route='/fdx/recipients' type='request' mdxType="Schema">
        <pre><code parentName="pre" {...{
            "className": "language-bash"
          }}>{`curl -X GET 'https://production.plaid.com/fdx/recipients' \\
  -H 'PLAID-CLIENT-ID: <client_id>' \\
  -H 'PLAID-SECRET: <secret>'
`}</code></pre>
      </Schema>
      <Schema method='GET' route='/fdx/recipients' type='response' mdxType="Schema" />
      <h3 {...{
        "id": "get-a-single-application-with-fdxrecipientrecipientid"
      }}>{`Get a single application with `}<inlineCode parentName="h3">{`/fdx/recipient/{recipientId}`}</inlineCode></h3>
      <p><inlineCode parentName="p">{`recipient_id`}</inlineCode>{` must be a UUID returned by `}<a parentName="p" {...{
          "href": "#get-a-list-of-applications-with-fdxrecipients"
        }}><inlineCode parentName="a">{`/fdx/recipients`}</inlineCode></a>{` — other values return an error. If this lookup happens during an active OAuth flow, you can optionally pass the OAuth `}<inlineCode parentName="p">{`state`}</inlineCode>{` value as an `}<inlineCode parentName="p">{`OAUTH-STATE-ID`}</inlineCode>{` header, which helps Plaid correlate the request if you need to troubleshoot it later.`}</p>
      <Schema method='GET' route='/fdx/recipient/{recipientId}' type='request' mdxType="Schema">
        <pre><code parentName="pre" {...{
            "className": "language-bash"
          }}>{`curl -X GET 'https://production.plaid.com/fdx/recipient/<recipient_id>' \\
  -H 'PLAID-CLIENT-ID: <client_id>' \\
  -H 'PLAID-SECRET: <secret>' \\
  -H 'OAUTH-STATE-ID: <oauth_state>'
`}</code></pre>
      </Schema>
      <Schema method='GET' route='/fdx/recipient/{recipientId}' type='response' mdxType="Schema" />
      <h2 {...{
        "id": "testing"
      }}>{`Testing`}</h2>
      <p>{`The App Directory API is supported in the Sandbox environment. To test, make calls against `}<inlineCode parentName="p">{`sandbox.plaid.com`}</inlineCode>{` instead of `}<inlineCode parentName="p">{`production.plaid.com`}</inlineCode>{`. Unlike Production, Sandbox data is static, so new records will not be created and existing records will not change.`}</p>
      <h2 {...{
        "id": "errors"
      }}>{`Errors`}</h2>
      <p>{`Both endpoints return errors in Plaid's standard `}<a parentName="p" {...{
          "href": "https://plaid.com/docs/errors/"
        }}>{`error object`}</a>{` format (`}<inlineCode parentName="p">{`error_type`}</inlineCode>{`, `}<inlineCode parentName="p">{`error_code`}</inlineCode>{`, `}<inlineCode parentName="p">{`error_message`}</inlineCode>{`, `}<inlineCode parentName="p">{`display_message`}</inlineCode>{`, `}<inlineCode parentName="p">{`request_id`}</inlineCode>{`). Log `}<inlineCode parentName="p">{`request_id`}</inlineCode>{` when you encounter an error to speed up troubleshooting with Plaid.`}</p>
    </div>
    </MDXLayout>;
}
;
MDXContent.isMDXComponent = true;

            export const tableOfContents = [
      {
        id: "overview",
        level: 2,
        title: "Overview",
      },
{
        id: "authentication",
        level: 2,
        title: "Authentication",
      },
{
        id: "endpoints",
        level: 2,
        title: "Endpoints",
      },
{
        id: "get-a-list-of-applications-with-fdxrecipients",
        level: 3,
        title: "Get a list of applications with /fdx/recipients",
      },
{
        id: "get-a-single-application-with-fdxrecipientrecipientid",
        level: 3,
        title: "Get a single application with /fdx/recipient/{recipientId}",
      },
{
        id: "testing",
        level: 2,
        title: "Testing",
      },
{
        id: "errors",
        level: 2,
        title: "Errors",
      }
];
          
layoutProps.tableOfContents = tableOfContents;
MDXContent.layoutProps = layoutProps;

MDXContent.layout = function (props) { return <DocsBeta {...props} /> };
